/* @jsxImportSource @emotion/react */
import React from 'react'
import { FormattedMessage } from 'react-intl'

import { buildGrainBackground, ImgSanity } from '@slite/components'

import { useRoutes } from '../routes'
import { Button } from './Button/Button'
import { Section } from './Section'
import { mediaQuerySmall } from './style'
import { WebsiteText } from './WebsiteText'

const GRAIN_BACKGROUND = buildGrainBackground({
  baseFrequency: 0.8,
  numOctaves: 4,
  exponent: 8,
  amplitude: 3,
  tileSize: 200,
})

type SectionTemplatesProps = {
  templates: {
    title: string
    description: string
    imageUrl: string
    slug: string
    color: string
  }[]
}

function TemplateItem({
  title,
  imageUrl,
  color,
  href,
}: {
  title: string
  imageUrl: string
  color: string
  href: string
}) {
  return (
    <a
      href={href}
      css={{ color: 'var(--dark)' }}
      className="grid-span-3-not-small grid-span-6"
    >
      <div
        className="height-full"
        css={{
          overflow: 'hidden',
          // On hover, slightly move it up
          transition: 'transform 0.2s',
          ':hover': {
            transform: 'translateY(-4px)',
          },
        }}
      >
        <div
          className={`flexbox flexbox-centered padding-vertical-32 padding-horizontal-32 border-shade-00 radius-larger background-${color}`}
          css={{
            height: 220,
            backgroundImage: GRAIN_BACKGROUND,
            backgroundBlendMode: 'screen',
            [mediaQuerySmall]: {
              height: 120,
            },
          }}
        >
          <ImgSanity
            src={imageUrl}
            className="inline-block height-full"
            alt=""
          />
        </div>
        <div className="padding-top-12 flexbox flexbox-column">
          <WebsiteText
            variant="smallTitle"
            className="line-clamp-3 padding-right-40"
            css={{
              fontSize: 18,
              lineHeight: 1.4,
            }}
          >
            {title}
          </WebsiteText>
        </div>
      </div>
    </a>
  )
}

export function SectionTemplates({ templates }: SectionTemplatesProps) {
  const routes = useRoutes()

  return (
    <Section background="moon-dust-lighter" containerVerticalPadding={80}>
      <div className="grid-12">
        <div className="column-text grid-span-8-not-small grid-span-12">
          <WebsiteText variant="title" className="padding-top-24">
            <FormattedMessage
              id="sections.templates.title"
              defaultMessage="Templates"
            />
          </WebsiteText>
          <WebsiteText variant="body" className="padding-top-12">
            <FormattedMessage
              id="sections.templates.description"
              defaultMessage="Get started in no time with templates of your choice."
            />
          </WebsiteText>
        </div>

        <div className="column-cta grid-span-4-not-small grid-span-12 flexbox flexbox-column flexbox-justify-end">
          <Button
            href={routes.templates.index()}
            variant="secondary"
            className="margin-left-auto margin-bottom-8"
          >
            <FormattedMessage
              id="sections.templates.seemore"
              defaultMessage="See more templates"
            />
          </Button>
        </div>
      </div>
      <div className="margin-top-40 grid-12 grid-gap-24">
        {templates.map((template, index) => (
          <TemplateItem
            key={index}
            title={template.title}
            imageUrl={template.imageUrl}
            color={template.color}
            href={routes.templates.detail({ slug: template.slug })}
          />
        ))}
      </div>
    </Section>
  )
}
